// Typed axios wrappers around /api/v1/announcements/* — used by the admin
// announcement pages instead of going through Refine's simple-rest provider
// (our response shapes don't match Refine's defaults).

import { api } from './api-client';

export type AnnouncementCategory = 'OUTAGE' | 'MAINTENANCE' | 'GENERAL' | 'SECURITY';

export interface PublicAnnouncement {
  id: string;
  cluster_id: string;
  author_user_id: string;
  category: AnnouncementCategory;
  title: string;
  body_md: string;
  starts_at: string | null;
  ends_at: string | null;
  require_ack: boolean;
  pinned: boolean;
  cover_image_url: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  is_read?: boolean;
  is_acked?: boolean;
}

export interface CreateAnnouncementInput {
  category: AnnouncementCategory;
  title: string;
  body_md: string;
  starts_at?: string;
  ends_at?: string;
  require_ack: boolean;
  pinned: boolean;
  cover_image_url?: string;
  publish_now: boolean;
}
export type UpdateAnnouncementInput = Partial<CreateAnnouncementInput>;

export async function listAnnouncements(params: {
  limit?: number;
  cursor?: string;
  category?: AnnouncementCategory;
  pinned_only?: boolean;
}): Promise<{ items: PublicAnnouncement[]; nextCursor: string | null }> {
  const { data } = await api.get<{ items: PublicAnnouncement[]; nextCursor: string | null }>(
    '/announcements',
    { params },
  );
  return data;
}

export async function getAnnouncement(id: string): Promise<PublicAnnouncement> {
  const { data } = await api.get<{ announcement: PublicAnnouncement }>(`/announcements/${id}`);
  return data.announcement;
}

export async function createAnnouncement(input: CreateAnnouncementInput): Promise<{
  announcement: PublicAnnouncement;
  notification?: { total_recipients: number };
}> {
  const { data } = await api.post<{
    announcement: PublicAnnouncement;
    notification?: { total_recipients: number };
  }>('/announcements', input);
  return data;
}

export async function updateAnnouncement(
  id: string,
  patch: UpdateAnnouncementInput,
): Promise<PublicAnnouncement> {
  const { data } = await api.patch<{ announcement: PublicAnnouncement }>(
    `/announcements/${id}`,
    patch,
  );
  return data.announcement;
}

export async function publishAnnouncement(id: string): Promise<{
  announcement: PublicAnnouncement;
  notification: { total_recipients: number };
}> {
  const { data } = await api.post<{
    announcement: PublicAnnouncement;
    notification: { total_recipients: number };
  }>(`/announcements/${id}/publish`);
  return data;
}

export async function deleteAnnouncement(id: string): Promise<void> {
  await api.delete(`/announcements/${id}`);
}
